import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
    MdOutlineEdit,
    MdOutlineCheckBoxOutlineBlank,
    MdOutlineCheckBox,
    MdOutlineInfo,
    MdOutlineInventory2
} from 'react-icons/md';
import Spinner from 'assets/gif/dots.gif';
import Header from 'components/Header';
import Button from 'components/Button';
import Table from 'components/Table';
import TextInput from 'components/TextInput';
import Tabs from 'components/Tabs';
import TestConfigPopup from 'components/TestConfigPopup';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import {
    SUBJECTS,
    buildInitialConfig,
    timeConstraintLabel
} from 'global/testConfig';

import './index.css';

const TEST_TYPE = {'SAT': 'Digital SAT', 'ACT': 'Digital ACT', 'PSAT': 'Digital PSAT', 'SHSAT': 'Digital SHSAT', 'TSIA2': 'TSIA2', 'BCA': 'BCA' };
const TEST_TYPE_PAPER = {'SAT': 'Digital SAT', 'ACT': 'Digital ACT', 'PSAT': 'Digital PSAT', 'SHSAT': 'Digital SHSAT', 'TSIA2': 'TSIA2', 'BCA': 'BCA', 'Paper ACT': 'Paper ACT' };

// From the 2nd re-assign on, assigning the test again costs a credit.
const REASSIGN_CREDIT_NOTICE = 'Additional credit will be charged from 2nd re-assign.';

function Tests() {
    const [tests, setTests] = useState([]);
    const [testIndex, setTestIndex] = useState(null);
    const [students, setStudents] = useState([]);
    // The student row whose config popup is open. 'assign' opens from an
    // empty checkbox, 'update' from the pencil on an unstarted assignment.
    const [configTarget, setConfigTarget] = useState<{ studentOrganizationId: number; mode: 'assign' | 'update' } | null>(null);
    const [search, setSearch] = useState('');
    const [testType, setTestType] = useState(Object.values(TEST_TYPE)[0]);
    const [tabIndex, setTabIndex] = useState(0);
    const [archiveSelected, setArchiveSelected] = useState(false);
    const [studentsLoading, setStudentsLoading] = useState(false);

    const { user } = useAuthContext();
    const navigate = useNavigate();

    const testTypes = user.customizations.find(customization => customization === 'Paper Test') ? TEST_TYPE_PAPER : TEST_TYPE;
    const filteredTestTypes = Object.fromEntries(
        Object.entries(testTypes).filter(([key, value]) => tests.find(test => test.type === value && !test.archived) || key === 'Paper ACT')
    );

    const hasArchived = tests.some(test => test.archived);
    const visibleTests = archiveSelected
        ? tests.filter(test => test.archived)
        : tests.filter(test => test.type === testType && !test.archived);

    const selectedTest = visibleTests[testIndex];
    const configStudent = configTarget && students.find(student => student.studentOrganizationId === configTarget.studentOrganizationId);

    const handleTestType = (value) => {
        setTestType(value);
        setTestIndex(null);
    }

    // Swap the server's refreshed row into the list.
    const replaceStudent = (row) => {
        setStudents(current => current.map(student => (
            student.studentOrganizationId === row.studentOrganizationId ? row : student
        )));
    }

    // Resolves once the row is updated; rejects with the server error so the
    // popup can show it.
    const submitConfig = (payload) => {
        const request = configTarget.mode === 'assign'
            ? axios.post('tutors/tests/assign/', {
                ...payload,
                action: 'assign',
                studentOrganizationId: configTarget.studentOrganizationId,
                testId: selectedTest.testId
            })
            : axios.post('tutors/tests/configure/', {
                ...payload,
                studentOrganizationId: configTarget.studentOrganizationId,
                testId: selectedTest.testId
            });

        return request.then(res => {
            replaceStudent(res.data.student);
            setConfigTarget(null);
        });
    }

    const unassignTest = (studentOrganizationId: number) => {
        axios.post('tutors/tests/assign/', {
                action: 'unassign',
                studentOrganizationId: studentOrganizationId,
                testId: selectedTest.testId
            })
            .then(res => replaceStudent(res.data.student))
            .catch(error => {
                console.log(error);
            });
    }

    useEffect(() => {
        axios.get(`tutors/tests/`)
            .then(res => {
                // console.log(res.data);
                setTests(res.data.tests);
                // setTestModules(res.data.testModules);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);


    useEffect(() => {
        // if (byModules && testModules.filter(testModule => testModule.type === testType)[testIndex]) {
        //     setStudentsLoading(true);
        //     setStudents([]);
        //     axios.get(`tutors/testModules/students/${testModules.filter(testModule => testModule.type === testType)[testIndex].testModuleId}`)
        //         .then(res => {
        //             setStudents(res.data.students);
        //             setStudentsLoading(false);
        //         })
        //         .catch(error => {
        //             console.log(error);
        //             setStudentsLoading(false);
        //         });
        // }
        if (visibleTests[testIndex]) {
            setStudentsLoading(true);
            setStudents([]);
            axios.get(`tutors/tests/students/${visibleTests[testIndex].testId}/`)
                .then(res => {
                    setStudents(res.data.students);
                    setStudentsLoading(false);
                })
                .catch(error => {
                    console.log(error);
                    setStudentsLoading(false);
                });
        }
    }, [testIndex]);

    return (
        <>
            <Helmet>
                <title>
                    All Tests - Test Center Admin
                </title>
            </Helmet>
            <Header title='All Tests' />
            <div className='tests'>
                {configTarget !== null && configStudent && selectedTest &&
                    <TestConfigPopup
                        test={selectedTest}
                        initialConfig={buildInitialConfig(
                            selectedTest,
                            configTarget.mode === 'update' ? configStudent : null,
                            parseFloat(configStudent.accomodation ?? 1)
                        )}
                        mode={configTarget.mode}
                        notice={configTarget.mode === 'assign' && configStudent.numTaken > 1 ? REASSIGN_CREDIT_NOTICE : undefined}
                        onSubmit={submitConfig}
                        onClose={() => setConfigTarget(null)}
                    />
                }
                <div className='tests__tests'>
                    <div className='tests__bar1'>
                        <p className='tests__text'>
                            Select a test and assign students
                        </p>
                        {tests.length > 0 &&
                            <div className='tests__bar'>
                                <Tabs
                                    // tabs={[
                                    //     <span className="tests__tabContent">
                                    //         SAT {tests.filter(test=> test.type==='Digital SAT').some(test=> test.isNew) && (<span className="tests__newBadge">NEW</span>)}
                                    //     </span>,
                                    //     <span className="tests__tabContent">
                                    //         ACT {tests.filter(test=> test.type==='Digital ACT').some(test=> test.isNew) && (<span className="tests__newBadge">NEW</span>)}
                                    //     </span>,
                                    //     <span className="tests__tabContent">
                                    //         PSAT {tests.filter(test=> test.type==='Digital PSAT').some(test=> test.isNew) && (<span className="tests__newBadge">NEW</span>)}
                                    //     </span>,
                                    // ]}
                                    tabs={[
                                        ...Object.keys(filteredTestTypes).map(tab => (
                                            <span className='tests__tabContent'>
                                                {tab} {tests.filter(test => test.type === filteredTestTypes[tab] && !test.archived).some(test => test.isNew) && (<span className='tests__newBadge'>NEW</span>)}
                                            </span>
                                        )),
                                        ...(hasArchived ? [
                                            <span className='tests__tabContent tests__tabContent--archive'>
                                                Archive
                                                <MdOutlineInventory2 className='tests__archiveTabIcon' />
                                            </span>
                                        ] : []),
                                    ]}
                                    tabIndex={tabIndex}
                                    onSelect={[
                                        ...Object.values(filteredTestTypes).map((_tab, t) => (
                                            () => {
                                                setTabIndex(t)
                                                setArchiveSelected(false)
                                                handleTestType(_tab)
                                            }
                                        )),
                                        ...(hasArchived ? [
                                            () => {
                                                setTabIndex(Object.keys(filteredTestTypes).length)
                                                setArchiveSelected(true)
                                                setTestIndex(null)
                                            }
                                        ] : []),
                                    ]}
                                    // value={testType}
                                    // onSelect={value => handleTestType(value)}
                                />
                                {(Object.keys(filteredTestTypes)[tabIndex] === 'Paper ACT' && testIndex === null) &&
                                    <Button onClick={() => navigate('/tests/upload')}>
                                        Upload
                                    </Button>
                                }
                                {/* {testIndex === null &&
                                    <ToggleSwitch 
                                        label='assign by module'
                                        toggle={byModules}
                                        onClick={() => setByModules(!byModules)}
                                    />
                                } */}
                            </div>
                        }
                    </div>
                    {archiveSelected && (
                        <div className='tests__archiveNote'>
                            <MdOutlineInfo />
                            <span>These are retired tests, kept available for ongoing assignments.</span>
                        </div>
                    )}
                    {/* jisoo: Add Test Preview Column */}
                    <Table
                        key={tabIndex}
                        className={testIndex === null && 'tests__table'}
                        header={[
                            // ... testIndex === null ? ['Preview'] : [],
                            'Name',
                            ... testIndex === null ? [
                                <p className='tests__cellCenter'>
                                    Avg Score
                                </p>
                            ] : [],
                            ... testIndex === null ? ['Type'] : [],
                            ... testIndex === null ? ['Assign'] : []
                        ]}
                        rows={visibleTests.map(test => ([
                            // ... testIndex === null ? ['View'] : [],
                            <span className="tests__testsContent">
                                {test.name} {test.isNew && <span className="tests__testsnewBadge">NEW</span>}
                            </span>,
                            ... testIndex === null ? [
                                <p className='tests__cellCenter'>
                                    {Math.round(test.testSections.reduce((sum, section) => sum + parseFloat(section.avgScore || 0), 0)/10)*10 || '-'}
                                </p>
                            ] : [],
                            ... testIndex === null ? [test.type] : [],
                            ... testIndex === null ? ['Assign'] : []
                        ]))}
                        onSelectRow={visibleTests.map((_test, t) => (
                            () => setTestIndex(testIndex === t ? null : t)
                        ))}
                        rowsSelected={visibleTests.map((test, t) => (
                            testIndex === t
                        ))}
                        //jisoo
                        onSelectCell={visibleTests.map((test, t) => ([
                            user.customizations.find(customization => customization === 'Disable Preview')
                                ? null
                                : testIndex !== null
                                ? () => setTestIndex(null)
                                : test.type === 'Digital SAT' || test.type === 'Digital PSAT'
                                ? () => navigate(`/tests/digital-sat/${test.testId}`)
                                : test.type === 'BCA'
                                ? () => navigate(`/tests/digital-bca/${test.testId}`)
                                : test.type === 'Digital ACT'
                                ? () => navigate(`/tests/digital-act/${test.testId}`)
                                : test.type === 'Digital SHSAT'
                                ? () => navigate(`/tests/digital-shsat/${test.testId}`)
                                : test.type === 'Paper ACT'
                                ? () => navigate(`/tests/paper-act/${test.testId}`)
                                : test.type === 'TSIA2'
                                ? () => navigate(`/tests/digital-tsia2/${test.testId}`)
                                : null,
                            null,
                            ... testIndex === null ? [null] : [],
                            ... testIndex === null ? [() => setTestIndex(testIndex === t ? null : t)] : [],
                        ]))}
                    />
                </div>
                {testIndex !== null && <>
                    {/* <AiOutlineDoubleRight className='tests__arrow' /> */}
                    <div className='tests__students'>
                        <div className='tests__bar'>
                            <TextInput
                                className='tests__search'
                                placeholder='search student'
                                value={search}
                                onChange={event => setSearch(event.target.value)}
                            />
                            <p className='tests__text'>
                                {students.filter(student => (
                                    student.testAssigned
                                )).length} {students.filter(student => (
                                    student.testAssigned
                                )).length > 1 
                                    ? 'students'
                                    : 'student'
                                } assigned
                            </p>
                        </div>
                        <Table
                            header={studentsLoading ? [''] : ['', 'Student', 'Sections', 'Timing', 'Attempts']}
                            columnTypes={studentsLoading ? [null] : [null, 'string', 'string', 'string', 'number']}
                            rows={studentsLoading ? [[<img className='tests__loader' src={Spinner} />]] : students.filter(student => (
                                !search ||
                                student.email?.toLowerCase().includes(search.toLowerCase()) ||
                                student.studentId?.toLowerCase().includes(search.toLowerCase()) ||
                                student.firstName?.toLowerCase().includes(search.toLowerCase()) ||
                                student.lastName?.toLowerCase().includes(search.toLowerCase())
                            )).map((student, s) => ([
                                student.testAssigned
                                    ?   <>
                                            <MdOutlineCheckBox
                                                className={student.inProgress ? 'tests__checkBox--disabled' : 'tests__checkBox'}
                                                onClick={() => !student.inProgress && unassignTest(student.studentOrganizationId)}
                                            />
                                            {student.inProgress
                                            ?   <img className='tests__loader' src={Spinner} />
                                            :   <MdOutlineEdit
                                                    className='tests__edit'
                                                    onClick={() => setConfigTarget({ studentOrganizationId: student.studentOrganizationId, mode: 'update' })}
                                                />
                                            }
                                        </>
                                    :   <MdOutlineCheckBoxOutlineBlank
                                            className='tests__checkBox--blank'
                                            onClick={() => setConfigTarget({ studentOrganizationId: student.studentOrganizationId, mode: 'assign' })}
                                        />,
                                `${student.firstName} ${student.lastName}`,
                                student.testAssigned ? student.testSections.map(testSection => (
                                    SUBJECTS[testSection.subject]
                                )).join(' + ') + (student.testSections.length === 1 ? ' only' : '') : null,
                                // student.testAssigned
                                //     ? student.testSections.find(testSection => testSection.moduleTwoDifficulty !== null) ? 'Fixed' : 'Adaptive'
                                //     : null,
                                student.testAssigned
                                    ? timeConstraintLabel(student.timeExtension)
                                    : null,
                                student.numTaken,
                                // student.email
                            ]))}
                            onSelectCell={!studentsLoading && students.filter(student => (
                                !search ||
                                student.email?.toLowerCase().includes(search.toLowerCase()) ||
                                student.studentId?.toLowerCase().includes(search.toLowerCase()) ||
                                student.firstName?.toLowerCase().includes(search.toLowerCase()) ||
                                student.lastName?.toLowerCase().includes(search.toLowerCase())
                            )).map((student, s) => ([
                                null,
                                () => navigate(`/students/${student.studentOrganizationId}`),
                                null,
                                null,
                                null
                            ]))}
                        />
                    </div>
                </>}
            </div>
        </>
    );
}

export default Tests;